import { lazy } from "react";
import {
	createBrowserRouter,
	createRoutesFromElements,
	Navigate,
	Outlet,
	Route,
	RouterProvider,
	useParams,
} from "react-router-dom";
import {
	AuthenticatedRoute,
	AuthorizedRoute,
	routes as authRoutes,
	FeatureFlaggedRoute,
	Licensed,
	LicensedRoute,
	useToken,
	useUser,
} from "@packsize/authentication";

import { LazyRoute } from "@/components/lazy-route";
import { LiveEnvironmentWrapper } from "@/components/live-environment-wrapper";
import { SiteContainer } from "@/components/site-container";
import { EntityHistoryProvider } from "@/contexts/entity-history";
import { environment } from "@/environment";
import { getLoginDestination } from "@/utils/get-login-destination";
import { Closing } from "@/views/closing/closing";
import { History as ClosingHistory } from "@/views/closing/history";
import ConfigurationNav from "@/views/configuration/configuration-nav";
import { CartonPropertyGroups } from "@/views/configuration/carton-property-groups";
import { Classifications } from "@/views/configuration/classifications";
import { ConfigurationAudit } from "@/views/configuration/configuration-audit";
import { MachineGroups } from "@/views/configuration/machine-groups";
import { MachineService } from "@/views/configuration/machine-service";
import { PickZones } from "@/views/configuration/pick-zones";
import { ProductionGroups } from "@/views/configuration/production-groups";
import { Roles } from "@/views/configuration/roles";
import { ServerSettings, ServerSettingsTabRoute } from "@/views/configuration/server-settings/server-settings";
import ApiSettingsTab from "@/views/configuration/server-settings/api-settings-tab";
import { OrdersAudit } from "@/views/configuration/orders-audit";
import {
	allAdministratorPermissions,
	allOperatorPermissions,
	scheduledReportPermissions,
} from "@/views/configuration/roles/role-utils";
import { OptimalProductionDetails } from "@/views/dashboard/optimal-production-details";
import RequireCurrentMachineGroup from "@/views/production/require-current-machine-group";
import { Kiosk } from "@/views/kiosk";
import { SiteSelect } from "@/views/login/site-select";
import { NotFound } from "@/views/not-found";
import { ManualMachineControl } from "@/views/production/manual-machine-control";
import Orders from "@/views/production/orders/orders";
import OrdersHistory from "@/views/production/orders/history/orders-history";
import ProductionNav from "@/views/production/production-nav";
import { SavedJobDependencyProvider } from "@/views/production/saved-jobs/saved-job-dependency-provider";
import ReportingNav from "@/views/reporting/reporting-nav";
import { ToolsPage } from "@/views/tools";
import { Users } from "@/views/configuration/users";

import InternalPreviewMessage from "./views/reporting/internal-preview-message";

const EditPackageSolution = lazy(() =>
	import("@/views/production/saved-jobs/views/edit-package-solution").then((m) => ({
		default: m.EditPackageSolution,
	})),
);
const CreatePackagingSolution = lazy(() =>
	import("@/views/production/saved-jobs/views/create-packaging-solution").then((m) => ({
		default: m.CreatePackagingSolution,
	})),
);
const LazyChangeCorrugate = lazy(() =>
	import("@/views/production/change-corrugate").then((m) => ({
		default: m.ChangeCorrugate,
	})),
);

const LazyDimensioningNav = lazy(() =>
	import("@/views/dimensioning").then((m) => ({
		default: m.DimensioningNav,
	})),
);
const LazyDimImportHistory = lazy(() =>
	import("@/views/dimensioning").then((m) => ({
		default: m.Import,
	})),
);
const LazyDimScanPage = lazy(() =>
	import("@/views/dimensioning").then((m) => ({
		default: m.ScanPage,
	})),
);
const LazyDisto = lazy(() =>
	import("@/views/dimensioning").then((m) => ({
		default: m.Disto,
	})),
);
const LazyDimProducts = lazy(() =>
	import("@/views/dimensioning").then((m) => ({
		default: m.Products,
	})),
);
const LazyDimConfigurationPage = lazy(() =>
	import("@/views/dimensioning").then((m) => ({
		default: m.ConfigurationPage,
	})),
);
const LazyStatus = lazy(() => import("@/views/status").then((m) => ({ default: m.Status })));
const LazyAuditTrail = lazy(() =>
	import("@/views/search/views/v1/audit-trail").then((m) => ({
		default: m.AuditTrail,
	})),
);
const DataMapping = lazy(() =>
	import("@/views/configuration/importing-data/views").then((m) => ({
		default: m.DataMapping,
	})),
);
const ImportSource = lazy(() =>
	import("@/views/configuration/importing-data/views").then((m) => ({
		default: m.ImportSource,
	})),
);
const LazyInvalidAccess = lazy(() =>
	import("@/views/login/invalid-access").then((m) => ({
		default: m.InvalidAccess,
	})),
);

const LazyMachines = lazy(() => import("@/views/configuration/machines").then((m) => ({ default: m.Machines })));
const LazyMachineForm = lazy(() => import("@/views/configuration/machines").then((m) => ({ default: m.MachineForm })));

const LazyPrint = lazy(() => import("@/views/configuration/print").then((m) => ({ default: m.PrintLayout })));
const LazyPrintImages = lazy(() =>
	import("@/views/configuration/print/images").then((m) => ({ default: m.PrintImages })),
);
const LazyPrintCampaigns = lazy(() =>
	import("@/views/configuration/print/campaigns").then((m) => ({ default: m.PrintCampaigns })),
);
const LazyMaterials = lazy(() => import("@/views/configuration/materials").then((m) => ({ default: m.Materials })));
const LazyMaterialsCorrugates = lazy(() =>
	import("@/views/configuration/materials/corrugates").then((m) => ({ default: m.Corrugates })),
);
const LazyMaterialsPackagingDesigns = lazy(() =>
	import("@/views/configuration/materials/packaging-designs").then((m) => ({ default: m.PackagingDesigns })),
);
const LazyMaterialsLabelTemplates = lazy(() =>
	import("@/views/configuration/materials/label-templates").then((m) => ({ default: m.LabelTemplates })),
);
const LazyAdvancedWorkflows = lazy(() =>
	import("@/views/configuration/advanced-workflows/advanced-workflows-layout").then((m) => ({
		default: m.AdvancedWorkflows,
	})),
);
const LazyAdvancedWorkflowsCartonPropertyGroups = lazy(() =>
	import("@/views/configuration/carton-property-groups").then((m) => ({ default: m.CartonPropertyGroups })),
);
const LazyAdvancedWorkflowsClassifications = lazy(() =>
	import("@/views/configuration/classifications").then((m) => ({ default: m.Classifications })),
);
const LazyAdvancedWorkflowsPickZones = lazy(() =>
	import("@/views/configuration/pick-zones").then((m) => ({ default: m.PickZones })),
);
const LazyEventNotificationsConfigurationView = lazy(() =>
	import("@/views/configuration/event-notifications").then((m) => ({ default: m.EventNotifications })),
);
const LazyEventNotificationFormPage = lazy(() =>
	import("@/views/configuration/event-notifications").then((m) => ({ default: m.EventNotificationFormPage })),
);
const LazyFailedNotificationsPage = lazy(() =>
	import("@/views/configuration/event-notifications").then((m) => ({ default: m.FailedNotificationsPage })),
);
const LazyPlannedProductionConfigurationView = lazy(() =>
	import("@/views/configuration/planned-production").then((p) => ({ default: p.PlannedProduction })),
);
const LazySecurePrintConfigurationView = lazy(() =>
	import("@/views/configuration/secure-print").then((m) => ({ default: m.SecurePrintConfigurationView })),
);
const PipelineWrapper = lazy(() =>
	import("@/views/configuration/importing-data/views").then((m) => ({
		default: m.PipelineWrapper,
	})),
);
const RulesConfiguration = lazy(() =>
	import("@/views/configuration/importing-data/views").then((m) => ({
		default: m.RulesConfiguration,
	})),
);
const SelectionAnalyticsDetails = lazy(() =>
	import("@/views/dashboard/dashboard-views").then((m) => ({ default: m.SelectionAnalyticsDetails })),
);
const SelectionHistoryDetails = lazy(() =>
	import("@/views/dashboard/dashboard-views").then((m) => ({ default: m.SelectionHistoryDetails })),
);
const Dashboard = lazy(() => import("@/views/dashboard/dashboard-views").then((m) => ({ default: m.Dashboard })));
const LazySupport = lazy(() => import("@/views/support").then((m) => ({ default: m.Support })));
const LazySavedJobs = lazy(() => import("@/views/production/saved-jobs"));
const LazyReportingCarton = lazy(() => import("@/views/reporting/carton"));
const LazyReportingMaterialUsage = lazy(() => import("@/views/reporting/material-usage"));
const LazyReportingTrim = lazy(() => import("@/views/reporting/trim"));
const LazyReportingErrors = lazy(() => import("@/views/reporting/errors-availability"));
const LazyReportingOee = lazy(() => import("@/views/reporting/oee"));
const LazyReportingRawData = lazy(() => import("@/views/reporting/raw-data"));
const LazyReportingScheduledReports = lazy(() => import("@/views/reporting/scheduled-reports"));

const LazySearchV1 = lazy(() => import("@/views/search/views/v1").then((m) => ({ default: m.Search })));
const LazySearchV2 = lazy(() => import("@/views/search/views/v2/search").then((m) => ({ default: m.Search })));
const LazySearchV2AuditTrailLayout = lazy(() =>
	import("@/views/search/views/v2/audit-trail/layout").then((m) => ({ default: m.Layout })),
);
const LazySearchV2Stage = lazy(() =>
	import("@/views/search/views/v2/audit-trail/stage").then((m) => ({ default: m.Stage })),
);
const LazySearchV2Cube = lazy(() =>
	import("@/views/search/views/v2/audit-trail/cube").then((m) => ({ default: m.Cube })),
);
const LazySearchV2Dim = lazy(() => import("@/views/search/views/v2/audit-trail/dim").then((m) => ({ default: m.Dim })));
const LazySearchV2Import = lazy(() =>
	import("@/views/search/views/v2/audit-trail/import").then((m) => ({ default: m.Import })),
);
const LazySearchV2Production = lazy(() =>
	import("@/views/search/views/v2/audit-trail/production").then((m) => ({ default: m.Production })),
);
const LazySearchV2Close = lazy(() =>
	import("@/views/search/views/v2/audit-trail/close").then((m) => ({ default: m.Close })),
);
const LazySearchV2Completed = lazy(() =>
	import("@/views/search/views/v2/audit-trail/completed").then((m) => ({ default: m.Completed })),
);

const SavedJobsRedirect = () => {
	const { "*": splat } = useParams();
	return <Navigate to={`/production/packaging-creation${splat ? `/${splat}` : ""}`} replace />;
};

const InitialRedirect = () => {
	const { currentUser } = useUser();
	const { decodedToken } = useToken();

	return <Navigate to={getLoginDestination(currentUser ?? null, decodedToken, null)} replace />;
};

const DimensioningIndex = () => {
	const { hasAnyRole } = useUser();

	if (hasAnyRole(["ProduceUsingSingleMode", "ProduceUsingMultipleMode"])) {
		return <Navigate to="scan" replace />;
	}

	if (hasAnyRole(["ProduceUsingDisto"])) {
		return <Navigate to="disto" replace />;
	}

	return <Navigate to="products" replace />;
};

const ReportingIndex = () => {
	const { hasAnyRole, hasLicense, isPacksizeUser, isPacksizePartner } = useUser();
	const hasProfessionalReporting =
		isPacksizeUser === true || isPacksizePartner === true || hasLicense("Reporting", "professional") === true;
	const hasEnterpriseReporting =
		isPacksizeUser === true || isPacksizePartner === true || hasLicense("Reporting", "enterprise") === true;

	if (hasAnyRole(["AccessCartonProductionReport"]) === true) {
		return <Navigate to="carton/production" replace />;
	}

	if (hasAnyRole(["AccessZFoldUsageReport"]) === true) {
		return <Navigate to="material-usage" replace />;
	}

	if (hasAnyRole(["AccessTrimReport"]) === true) {
		return <Navigate to="trim" replace />;
	}

	if (hasAnyRole(["AccessMachineErrorsReport"]) === true && hasProfessionalReporting === true) {
		return <Navigate to="errors" replace />;
	}

	if (hasAnyRole(["AccessOverallEquipmentEffectiveness"]) === true && hasEnterpriseReporting === true) {
		return <Navigate to="oee" replace />;
	}

	if (hasAnyRole(["AccessRawDataReport"]) === true && hasProfessionalReporting === true) {
		return <Navigate to="raw-data" replace />;
	}

	if (hasAnyRole(scheduledReportPermissions) === true) {
		return <Navigate to="scheduled-reports" replace />;
	}

	return <Navigate to="carton/production" replace />;
};

const RouterLayout = () => (
	<>
		<Outlet />
		<LiveEnvironmentWrapper />
	</>
);

const appRoutes = createRoutesFromElements(
	<>
		<Route element={<RouterLayout />}>
			{/* Auth package routes (login, password-reset, etc.) */}
			{authRoutes.map((route) => (
				<Route key={route.path} path={route.path} element={route.element} />
			))}

			{/* Public/semi-public routes */}
			<Route path="/walmart-kiosk" element={<Kiosk />} />
			<Route path="/invalid-access" element={<LazyInvalidAccess />} />
			<Route
				path="/site-select"
				element={
					<AuthenticatedRoute>
						<SiteSelect />
					</AuthenticatedRoute>
				}
			/>

			{/* Protected app routes */}
			<Route
				path="/"
				element={
					<AuthenticatedRoute>
						<SiteContainer />
					</AuthenticatedRoute>
				}
			>
				<Route index element={<InitialRedirect />} />
				<Route path="support" element={<LazyRoute Component={LazySupport} />} />
				<Route path="status" element={<LazyRoute Component={LazyStatus} />} />

				<Route
					path="dashboard"
					element={
						<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-dashboard">
							<AuthorizedRoute permissions="AccessDashboard">
								<Outlet />
							</AuthorizedRoute>
						</LicensedRoute>
					}
				>
					<Route index element={<LazyRoute Component={Dashboard} />} />
					<Route path="optimal-production" element={<OptimalProductionDetails />} />
					<Route
						path="selection-analytics-details/:productionGroupId"
						element={<LazyRoute Component={SelectionAnalyticsDetails} />}
					/>
					<Route
						path="selection-history-details/:auditId"
						element={<LazyRoute Component={SelectionHistoryDetails} />}
					/>
					<Route
						path="packaging-solution-audit-trail/:packagingSolutionId"
						element={<LazyRoute Component={LazyAuditTrail} />}
					/>
				</Route>

				<Route
					path="production"
					element={
						<AuthorizedRoute permissions={allOperatorPermissions.map((p) => p.name)}>
							<ProductionNav />
						</AuthorizedRoute>
					}
				>
					<Route index element={<Navigate to="orders" replace />} />
					<Route path="orders" element={<Orders />} />
					<Route path="orders/history" element={<OrdersHistory />} />

					<Route
						path="change-corrugate"
						element={
							<AuthorizedRoute permissions="ChangeLoadedCorrugatesOnMachine">
								<LazyRoute Component={LazyChangeCorrugate} />
							</AuthorizedRoute>
						}
					/>
					<Route
						path="machine-control"
						element={
							<AuthorizedRoute permissions="AccessMachineControl">
								<ManualMachineControl />
							</AuthorizedRoute>
						}
					/>

					<Route path="saved-jobs/*" element={<SavedJobsRedirect />} />

					<Route
						path="packaging-creation"
						element={
							<AuthorizedRoute
								permissions={[
									"ManagePackagingSolutions",
									"ModifyAndSendPackagingSolutionToQueue",
									"SendPackagingSolutionToQueue",
									"CreatePackagingSolution",
								]}
							>
								<EntityHistoryProvider>
									<SavedJobDependencyProvider>
										<Outlet />
									</SavedJobDependencyProvider>
								</EntityHistoryProvider>
							</AuthorizedRoute>
						}
					>
						<Route index element={<LazyRoute Component={LazySavedJobs} />} />
						<Route path="create" element={<LazyRoute Component={CreatePackagingSolution} />} />
						<Route path=":id" element={<LazyRoute Component={EditPackageSolution} />} />
					</Route>

					<Route path="*" element={<Navigate to="/production" />} />
				</Route>

				<Route
					path="closing"
					element={
						<AuthorizedRoute permissions={allOperatorPermissions.map((p) => p.name)}>
							<Closing />
						</AuthorizedRoute>
					}
				/>
				<Route
					path="closing/history"
					element={
						<AuthorizedRoute permissions={allOperatorPermissions.map((p) => p.name)}>
							<ClosingHistory />
						</AuthorizedRoute>
					}
				/>

				<Route
					path="configuration"
					element={
						<AuthorizedRoute permissions={allAdministratorPermissions.map((p) => p.name)}>
							<EntityHistoryProvider>
								<ConfigurationNav />
							</EntityHistoryProvider>
						</AuthorizedRoute>
					}
				>
					<Route index element={<Navigate to="server-settings" replace />} />
					<Route
						path="server-settings"
						element={
							<AuthorizedRoute permissions={["ManageSettings"]}>
								<ServerSettings />
							</AuthorizedRoute>
						}
					>
						<Route index element={<Navigate to="settings" replace />} />
						<Route path="settings" element={<ServerSettingsTabRoute />} />
						<Route path="api" element={<ApiSettingsTab />} />
					</Route>
					<Route
						key="import-pipeline"
						path="import-pipeline"
						element={
							<AuthorizedRoute permissions={["manageimportpipelines"]}>
								<LazyRoute Component={PipelineWrapper} />
							</AuthorizedRoute>
						}
					>
						<Route path="import-source" element={<LazyRoute Component={ImportSource} />} />
						<Route path="data-mapping" element={<LazyRoute Component={DataMapping} />} />
						<Route path="rules-configuration" element={<LazyRoute Component={RulesConfiguration} />} />
					</Route>
					<Route
						path="machines"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-machines">
								<AuthorizedRoute permissions={["ManageMachines"]}>
									<Outlet />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					>
						<Route index element={<LazyRoute Component={LazyMachines} />} />
						<Route path="new" element={<LazyRoute Component={LazyMachineForm} />} />
						<Route path=":machineId" element={<LazyRoute Component={LazyMachineForm} />} />
					</Route>
					<Route
						path="materials"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-materials">
								<AuthorizedRoute permissions={["ManageCorrugates", "ManageDesigns", "ManageLabelTemplates"]}>
									<LazyRoute Component={LazyMaterials} />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					>
						<Route index element={<Navigate to="corrugates" replace />} />
						<Route path="corrugates" element={<LazyRoute Component={LazyMaterialsCorrugates} />} />
						<Route path="packaging-designs" element={<LazyRoute Component={LazyMaterialsPackagingDesigns} />} />
						<Route path="label-templates" element={<LazyRoute Component={LazyMaterialsLabelTemplates} />} />
					</Route>
					<Route
						path="advanced-workflows"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-materials">
								<AuthorizedRoute permissions={["ManageCorrugates", "ManageDesigns", "ManageLabelTemplates"]}>
									<LazyRoute Component={LazyAdvancedWorkflows} />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					>
						<Route index element={<Navigate to="carton-property-groups" replace />} />
						<Route
							path="carton-property-groups"
							element={<LazyRoute Component={LazyAdvancedWorkflowsCartonPropertyGroups} />}
						/>
						<Route path="classifications" element={<LazyRoute Component={LazyAdvancedWorkflowsClassifications} />} />
						<Route path="pick-zones" element={<LazyRoute Component={LazyAdvancedWorkflowsPickZones} />} />
					</Route>
					<Route
						path="print"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-materials">
								<AuthorizedRoute permissions={["ManagePrintImages"]}>
									<LazyRoute Component={LazyPrint} />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					>
						<Route index element={<Navigate to="images" replace />} />
						<Route path="images" element={<LazyRoute Component={LazyPrintImages} />} />
						<Route path="campaigns" element={<LazyRoute Component={LazyPrintCampaigns} />} />
					</Route>
					<Route
						path="machine-groups"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-machine-groups">
								<AuthorizedRoute permissions={["ManageMachineGroups"]}>
									<MachineGroups />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="production-groups"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-production-groups">
								<AuthorizedRoute permissions={["ManageProductionGroups"]}>
									<ProductionGroups />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="machine-service"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-machines">
								<AuthorizedRoute permissions={["AccessServiceModePage"]}>
									<MachineService />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="classifications"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-classifications">
								<AuthorizedRoute permissions={["ManageClassifications"]}>
									<Classifications />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="carton-property-groups"
						element={
							<LicensedRoute
								productCategory="Core"
								tier="basic"
								licenseFeatureFlag="ff-pack-net-carton-property-groups"
							>
								<AuthorizedRoute permissions={["ManageCPGs"]}>
									<CartonPropertyGroups />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="pick-zones"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-pick-zones">
								<AuthorizedRoute permissions={["ManagePickZones"]}>
									<PickZones />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="roles"
						element={
							<AuthorizedRoute permissions={["ManageRoles"]}>
								<Roles />
							</AuthorizedRoute>
						}
					/>
					<Route
						path="users"
						element={
							<AuthorizedRoute permissions={["ManageUsers"]}>
								<Users />
							</AuthorizedRoute>
						}
					/>
					<Route
						path="event-notifications"
						element={
							<LicensedRoute productCategory="Core" tier="basic" licenseFeatureFlag="ff-pack-net-event-notifications">
								<AuthorizedRoute permissions={["ManageEventNotifications"]}>
									<Outlet />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					>
						<Route index element={<LazyRoute Component={LazyEventNotificationsConfigurationView} />} />
						<Route path="create" element={<LazyRoute Component={LazyEventNotificationFormPage} />} />
						<Route
							path=":subscriptionId/failed-notifications"
							element={<LazyRoute Component={LazyFailedNotificationsPage} />}
						/>
						<Route path=":id" element={<LazyRoute Component={LazyEventNotificationFormPage} />} />
					</Route>
					<Route
						path="planned-production"
						element={
							<LicensedRoute
								productCategory="Reporting"
								tier="professional"
								bypassForPacksizeUser
								bypassForPacksizePartner
							>
								<AuthorizedRoute permissions={["ManagePlannedProduction"]}>
									<LazyRoute Component={LazyPlannedProductionConfigurationView} />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="secure-print"
						element={
							<FeatureFlaggedRoute featureFlag="machines-allow-secure-print">
								<AuthorizedRoute permissions={["ManageImportPipelines"]}>
									<LazyRoute Component={LazySecurePrintConfigurationView} />
								</AuthorizedRoute>
							</FeatureFlaggedRoute>
						}
					/>
					<Route
						path="orders-audit"
						element={
							environment !== "prod1" && environment !== "legacy-prod1" ? (
								<AuthorizedRoute permissions={["accessOrdersAudit"]}>
									<OrdersAudit />
								</AuthorizedRoute>
							) : (
								<Navigate to="/configuration/server-settings" replace />
							)
						}
					/>
					<Route
						path="configuration-audit"
						element={
							<AuthorizedRoute permissions={["AccessConfigurationAudit"]}>
								<ConfigurationAudit />
							</AuthorizedRoute>
						}
					/>
					<Route path="*" element={<Navigate to="/configuration/server-settings" replace />} />
				</Route>

				<Route
					path="dimensioning"
					element={
						<LicensedRoute productCategory="DIM" tier="essential">
							<AuthorizedRoute
								permissions={[
									"ViewProducts",
									"EditConfiguration",
									"ProduceUsingDisto",
									"ProduceUsingSingleMode",
									"ProduceUsingMultipleMode",
								]}
							>
								<LazyRoute Component={LazyDimensioningNav} />
							</AuthorizedRoute>
						</LicensedRoute>
					}
				>
					<Route index element={<DimensioningIndex />} />
					<Route
						path="scan"
						element={
							<LicensedRoute productCategory="DIM" tier="essential">
								<AuthorizedRoute permissions={["ProduceUsingSingleMode", "ProduceUsingMultipleMode"]}>
									<RequireCurrentMachineGroup>
										<LazyRoute Component={LazyDimScanPage} />
									</RequireCurrentMachineGroup>
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="disto"
						element={
							<LicensedRoute productCategory="DIM" tier="essential">
								<AuthorizedRoute permissions={["ProduceUsingDisto"]}>
									<RequireCurrentMachineGroup>
										<LazyRoute Component={LazyDisto} />
									</RequireCurrentMachineGroup>
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="products"
						element={
							<LicensedRoute productCategory="DIM" tier="essential">
								<AuthorizedRoute permissions={["ViewProducts"]}>
									<LazyRoute Component={LazyDimProducts} />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="products/importHistory"
						element={
							<LicensedRoute productCategory="DIM" tier="essential">
								<AuthorizedRoute permissions={["ViewProducts"]}>
									<LazyRoute Component={LazyDimImportHistory} />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="dim-config"
						element={
							<LicensedRoute productCategory="DIM" tier="essential">
								<AuthorizedRoute permissions={["EditConfiguration"]}>
									<LazyRoute Component={LazyDimConfigurationPage} />
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
				</Route>

				<Route
					path="reporting"
					element={
						<LicensedRoute productCategory="Reporting" tier="basic" bypassForPacksizeUser bypassForPacksizePartner>
							<ReportingNav />
						</LicensedRoute>
					}
				>
					<Route index element={<ReportingIndex />} />
					<Route
						path="carton/*"
						element={
							<AuthorizedRoute permissions={["AccessCartonProductionReport"]}>
								<LazyRoute>
									<Licensed
										productCategory="Reporting"
										tier="basic"
										fallback={<InternalPreviewMessage tier="basic" />}
									/>

									<LazyReportingCarton />
								</LazyRoute>
							</AuthorizedRoute>
						}
					/>
					<Route
						path="material-usage"
						element={
							<AuthorizedRoute permissions={["AccessZFoldUsageReport"]}>
								<LazyRoute>
									<Licensed
										productCategory="Reporting"
										tier="basic"
										fallback={<InternalPreviewMessage tier="basic" />}
									/>

									<LazyReportingMaterialUsage />
								</LazyRoute>
							</AuthorizedRoute>
						}
					/>
					<Route
						path="trim"
						element={
							<AuthorizedRoute permissions={["AccessTrimReport"]}>
								<LazyRoute>
									<Licensed
										productCategory="Reporting"
										tier="basic"
										fallback={<InternalPreviewMessage tier="basic" />}
									/>
									<LazyReportingTrim />
								</LazyRoute>
							</AuthorizedRoute>
						}
					/>
					<Route
						path="errors"
						element={
							<LicensedRoute
								productCategory="Reporting"
								tier="professional"
								bypassForPacksizeUser
								bypassForPacksizePartner
							>
								<AuthorizedRoute permissions={["AccessMachineErrorsReport"]}>
									<LazyRoute>
										<Licensed
											productCategory="Reporting"
											tier="professional"
											fallback={<InternalPreviewMessage tier="professional" />}
										/>

										<LazyReportingErrors />
									</LazyRoute>
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="oee"
						element={
							<LicensedRoute
								productCategory="Reporting"
								tier="enterprise"
								bypassForPacksizeUser
								bypassForPacksizePartner
							>
								<AuthorizedRoute permissions={["AccessOverallEquipmentEffectiveness"]}>
									<LazyRoute>
										<Licensed
											productCategory="Reporting"
											tier="enterprise"
											fallback={<InternalPreviewMessage tier="enterprise" />}
										/>

										<LazyReportingOee />
									</LazyRoute>
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="raw-data"
						element={
							<LicensedRoute
								productCategory="Reporting"
								tier="professional"
								bypassForPacksizeUser
								bypassForPacksizePartner
							>
								<AuthorizedRoute permissions={["AccessRawDataReport"]}>
									<LazyRoute>
										<Licensed
											productCategory="Reporting"
											tier="professional"
											fallback={<InternalPreviewMessage tier="professional" />}
										/>

										<LazyReportingRawData />
									</LazyRoute>
								</AuthorizedRoute>
							</LicensedRoute>
						}
					/>
					<Route
						path="scheduled-reports"
						element={
							<AuthorizedRoute permissions={scheduledReportPermissions}>
								<LazyRoute>
									<Licensed
										productCategory="Reporting"
										tier="basic"
										fallback={<InternalPreviewMessage tier="basic" />}
									/>

									<LazyReportingScheduledReports />
								</LazyRoute>
							</AuthorizedRoute>
						}
					/>
					<Route path="z-Fold" element={<Navigate to="../material-usage" replace />} />
				</Route>

				<Route path="simulators" element={<Navigate to="/tools" replace />} />
				<Route path="feature-flags" element={<Navigate to="/tools" replace />} />
				<Route
					path="/tools"
					element={
						<FeatureFlaggedRoute featureFlag="architecture-allow-packnet-tools">
							<ToolsPage />
						</FeatureFlaggedRoute>
					}
				/>

				<Route path="search">
					<Route path="v2">
						<Route index element={<LazyRoute Component={LazySearchV2} />} />
						<Route path=":batchId" element={<LazyRoute Component={LazySearchV2AuditTrailLayout} />}>
							<Route path="cube" element={<LazyRoute Component={LazySearchV2Cube} />} />
							<Route path="dim" element={<LazyRoute Component={LazySearchV2Dim} />} />
							<Route path="import" element={<LazyRoute Component={LazySearchV2Import} />} />
							<Route path="stage" element={<LazyRoute Component={LazySearchV2Stage} />} />
							<Route path="production" element={<LazyRoute Component={LazySearchV2Production} />} />
							<Route path="close" element={<LazyRoute Component={LazySearchV2Close} />} />
							<Route path="completed" element={<LazyRoute Component={LazySearchV2Completed} />} />
						</Route>
					</Route>
					{/* V1 search routes */}
					<Route index element={<LazyRoute Component={LazySearchV1} />} />
					<Route path=":packagingSolutionId" element={<LazyRoute Component={LazyAuditTrail} />} />
				</Route>
				<Route path="*" element={<NotFound />} />
			</Route>

			<Route
				path="*"
				element={
					<AuthenticatedRoute>
						<NotFound />
					</AuthenticatedRoute>
				}
			/>
		</Route>
	</>,
);

const appRouter = createBrowserRouter(appRoutes, { basename: import.meta.env?.VITE_BASENAME });

export const AppRoutes = () => <RouterProvider router={appRouter} />;
